import React from "react";
import { EyeOff, ShieldAlert, Check } from "lucide-react";
import { resolveImg } from "../../lib/api";
import { useLabText } from "../../lib/labText";

export const ExperimentCard = ({ e, purchased, evalState, onEvaluate, voices, showSingle, stages = {} }) => {
  const { t, pick, aed } = useLabText();
  const evalDone = (stages.stage2_enabled ? evalState?.stage2 === "submitted" : evalState?.stage1 === "submitted") || (!stages.stage1_enabled && !stages.stage2_enabled);
  const img = e.images?.[0];
  const notes = pick(e, "notes");
  const safety = [pick(e, "safety"), pick(e, "usage")].filter(Boolean);
  return (
    <article data-testid={`lab-exp-card-${e.code}`} className="border border-[#262421] bg-[#0F0E0C] flex flex-col">
      <div className="aspect-[4/5] bg-[#141311] overflow-hidden relative">
        {img ? <img src={resolveImg(img)} alt={pick(e, "name")} className="w-full h-full object-cover" loading="lazy" />
          : <div className="w-full h-full flex items-center justify-center serif text-4xl tracking-[0.2em] text-[#3A3630]" dir="ltr">{e.code}</div>}
        <span className="absolute top-3 start-3 bg-[#0B0A08]/80 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-[0.25em] text-[#C49A6C]" dir="ltr">{e.code}</span>
      </div>
      <div className="p-5 flex-1 flex flex-col gap-3">
        <div>
          <h3 className="serif text-2xl text-[#F2EBE1]">{pick(e, "name")}</h3>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#8A857D] mt-1" dir="ltr">{e.size_ml} ml{showSingle && e.price ? ` · ${aed(e.price)} · ${e.available} ${t("left")}` : ""}</p>
        </div>
        {pick(e, "description") && <p className="text-sm text-[#B8B0A4] leading-relaxed whitespace-pre-line">{pick(e, "description")}</p>}
        {e.blind ? (
          <p className="flex items-start gap-2 text-xs text-[#8A857D] border-s border-[#C49A6C]/40 ps-3"><EyeOff size={14} className="mt-0.5 shrink-0" />{t("blind")}</p>
        ) : (
          <>
            {Array.isArray(notes) && notes.length > 0 && <p className="text-xs text-[#C9BFAF]"><span className="text-[#8A857D] uppercase tracking-[0.2em] me-2">{t("notes")}</span>{notes.join(" · ")}</p>}
            {pick(e, "concept") && <p className="text-sm text-[#B8B0A4] italic leading-relaxed whitespace-pre-line">{pick(e, "concept")}</p>}
          </>
        )}
        <div className="border border-[#3A2E22] bg-[#15110C] p-3 mt-auto" data-testid={`lab-safety-${e.code}`}>
          <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#C49A6C]"><ShieldAlert size={13} />{e.eval_category === "skin" ? t("skin") : t("olfactory")}</p>
          {safety.map((s, i) => <p key={`${i}-${s.slice(0, 24)}`} className="text-xs text-[#A39A8D] mt-1.5 leading-relaxed whitespace-pre-line">{s}</p>)}
        </div>
        {purchased && (
          <button onClick={() => !evalDone && onEvaluate(e)} disabled={evalDone} data-testid={`lab-evaluate-${e.code}`}
            className="flex items-center justify-center gap-2 border border-[#C49A6C] text-[#C49A6C] text-[11px] uppercase tracking-[0.22em] py-3 hover:bg-[#C49A6C] hover:text-[#0B0A08] transition-colors">
            {evalDone ? <><Check size={14} />{t("done")}</> : evalState?.stage1 === "submitted" ? t("continue_stage2") : evalState?.stage1 || evalState?.stage2 ? t("continue_eval") : t("evaluate")}
          </button>
        )}
        {voices?.length > 0 && (
          <div className="border-t border-[#262421] pt-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A857D] mb-2">{t("voices")}</p>
            {voices.slice(0, 3).map((v, i) => <p key={`${i}-${v.name || ""}-${(v.quote || "").slice(0, 24)}`} className="text-xs text-[#B8B0A4] italic mb-1.5">“{v.quote}”{v.name ? ` — ${v.name}` : ""}</p>)}
          </div>
        )}
      </div>
    </article>
  );
};
